import { useState } from "react";
import { Link, useParams } from "react-router";
import { Aviso, Botao, Cartao } from "../componentes/ui";
import { ContatosDaOrganizacao, EnderecosDaOrganizacao } from "../componentes/cadastro/ContatosEEnderecos";
import { PoliticaDaOrganizacao } from "../componentes/acesso/PoliticaDaOrganizacao";
import { DadosDaOrganizacao } from "../componentes/cadastro/DadosDaOrganizacao";
import { FusaoEmCurso } from "../componentes/cadastro/FusaoEmCurso";
import { PessoasDaOrganizacao } from "../componentes/cadastro/PainelDePessoas";
import { mensagemDoConsole } from "../nucleo/console";
import {
  nomeDaOrganizacao,
  useOrganizacaoDoCadastro,
  useOrganizacoesDoCadastro,
} from "../nucleo/cadastro";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

/** A ficha de uma organização para a equipe: dados, contatos, endereços, pessoas e fusão. */
export function DetalheDaOrganizacao() {
  const { id = "" } = useParams();
  const organizacao = useOrganizacaoDoCadastro(id);
  const sessao = useSessao();
  useTitulo(organizacao.data ? nomeDaOrganizacao(organizacao.data) : "Organização");

  if (organizacao.isPending) return <p role="status">Carregando a organização…</p>;
  if (organizacao.isError) {
    return (
      <>
        <Aviso tom="erro">{mensagemDoConsole(organizacao.error)}</Aviso>
        <p className="mt-3">
          <Link to="/admin/cadastros">Voltar aos cadastros</Link>
        </p>
      </>
    );
  }

  const podeEditar = podeNaSessao(sessao.data, "implantacao:editar");
  const podeGerenciarPessoas = podeNaSessao(sessao.data, "usuarios:gerenciar");
  const podeFundir = podeNaSessao(sessao.data, "cadastro:fundir");
  const podeConfigurarSeguranca = podeNaSessao(sessao.data, "seguranca:gerenciar");

  return (
    <div data-testid="detalhe-da-organizacao">
      <h1 className="titulo-pagina">{nomeDaOrganizacao(organizacao.data)}</h1>
      <p className="lead mb-4">
        <Link to="/admin/cadastros">Voltar aos cadastros</Link>
        {podeNaSessao(sessao.data, "sucesso:ver") ? (
          <>
            {" · "}
            <Link to={`/admin/sucesso/${id}`} data-testid="ver-a-saude-do-cliente">
              Ver a saúde do cliente
            </Link>
          </>
        ) : null}
      </p>
      <div className="grid grid-cols-1 gap-4">
        <DadosDaOrganizacao key={organizacao.data.id} organizacao={organizacao.data} podeEditar={podeEditar} />
        <ContatosDaOrganizacao organizacaoId={id} podeEditar={podeEditar} />
        <EnderecosDaOrganizacao organizacaoId={id} podeEditar={podeEditar} />
        <section aria-labelledby="titulo-pessoas-da-organizacao">
          <h2 className="titulo-secao" id="titulo-pessoas-da-organizacao">
            Pessoas
          </h2>
          <PessoasDaOrganizacao organizacaoId={id} podeEditar={podeGerenciarPessoas} />
        </section>
        {podeConfigurarSeguranca ? (
          <section aria-labelledby="titulo-politica-de-acesso">
            <h2 className="titulo-secao" id="titulo-politica-de-acesso">
              Política de acesso
            </h2>
            <Cartao>
              <PoliticaDaOrganizacao organizacaoId={id} />
            </Cartao>
          </section>
        ) : null}
        {podeFundir ? <FusaoDeOutraOrganizacao destino={{ id, nome: nomeDaOrganizacao(organizacao.data) }} /> : null}
      </div>
    </div>
  );
}

function FusaoDeOutraOrganizacao({ destino }: { destino: { id: string; nome: string } }) {
  const organizacoes = useOrganizacoesDoCadastro();
  const [origemId, setOrigemId] = useState("");
  /* O que está em confirmação fica guardado à parte: ao fundir, a lista de organizações é
     recarregada e a absorvida some dela — sem este retrato a tela de sucesso desmontava junto. */
  const [confirmando, setConfirmando] = useState<{ id: string; nome: string } | null>(null);
  const origem = organizacoes.data?.find((item) => item.id === origemId);

  return (
    <Cartao titulo="Fundir uma organização duplicada nesta" data-testid="fundir-organizacao">
      <p className="mb-3">
        Escolha a organização duplicada: os chamados, pessoas, ativos e o histórico dela passam
        para <b>{destino.nome}</b>.
      </p>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="origem-da-fusao">
          Organização a absorver
        </label>
        <select
          id="origem-da-fusao"
          className="campo__controle"
          value={origemId}
          onChange={(evento) => {
            setOrigemId(evento.target.value);
            setConfirmando(null);
          }}
        >
          <option value="">Escolha…</option>
          {(organizacoes.data ?? [])
            .filter((item) => item.id !== destino.id)
            .map((item) => (
              <option key={item.id} value={item.id}>
                {nomeDaOrganizacao(item)} ({item.cnpj_formatado})
              </option>
            ))}
        </select>
      </div>
      {origem && !confirmando ? (
        <Botao
          onClick={() => setConfirmando({ id: origem.id, nome: nomeDaOrganizacao(origem) })}
          data-testid="ver-previa-da-fusao"
        >
          Ver o que será movido
        </Botao>
      ) : null}
      {confirmando ? (
        <FusaoEmCurso
          tipo="organizacoes"
          destino={destino}
          origem={confirmando}
          aoCancelar={() => setConfirmando(null)}
          aoConcluir={() => {
            setConfirmando(null);
            setOrigemId("");
          }}
        />
      ) : null}
    </Cartao>
  );
}
